import { PhotoCamera } from "@mui/icons-material";
import { Box, Tooltip } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useAlert } from "react-alert";
import ImageUploading, { ImageListType } from "react-images-uploading";
import { Status } from "../../../../client/status";
import { SpinnerIcon } from "../../../../design-system/Icons";
import { useUploadImageClient } from "./client/uploadImageClient";

const MAX_IMAGE_SIZE = 26214400;

const thumbnailBoxSx = {
  width: 40,
  height: 40,
  flexShrink: 0,
  borderRadius: 1,
  overflow: "hidden",
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

export const UploadItemImage = ({
  src,
  itemId,
}: {
  src: string | undefined;
  itemId: number;
}) => {
  const [images, setImages] = useState<ImageListType>([]);
  const { uploadItemImage, status } = useUploadImageClient(itemId);
  const alert = useAlert();

  const previewImageSrc = images.length === 0 ? src : images[0].dataURL;

  const onImageSelect = (imageList: ImageListType) => {
    const isImageSizeExceeded =
      imageList[0].file && imageList[0].file.size > MAX_IMAGE_SIZE;
    if (isImageSizeExceeded) {
      alert.error("Max file size 25mb");
      return;
    }
    setImages(imageList);
    imageList[0].file && uploadItemImage(imageList[0].file);
  };

  useEffect(() => {
    status === Status.Error && alert.error("Upload failed, please try again");
  }, [alert, status]);

  if (status === Status.Pending) {
    return (
      <Box
        data-testid="test-id-upload-spinner"
        sx={{
          ...thumbnailBoxSx,
          bgcolor: "grey.100",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <SpinnerIcon />
      </Box>
    );
  }

  return (
    <ImageUploading value={images} onChange={onImageSelect}>
      {({ onImageUpload }) => (
        <Tooltip title={previewImageSrc ? "Change image" : "Add image"}>
          <Box
            onClick={onImageUpload}
            data-testid="test-id-upload-button"
            sx={{
              ...thumbnailBoxSx,
              cursor: "pointer",
              bgcolor: previewImageSrc ? "transparent" : "grey.100",
              border: previewImageSrc ? "none" : "1px dashed",
              borderColor: "grey.300",
              "&:hover .img-overlay": { opacity: 1 },
            }}
          >
            {previewImageSrc ? (
              <>
                <img
                  alt="item image"
                  src={previewImageSrc}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
                <Box
                  className="img-overlay"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    bgcolor: "rgba(0,0,0,0.42)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: 0,
                    transition: "opacity 0.15s",
                  }}
                >
                  <PhotoCamera sx={{ fontSize: 16, color: "white" }} />
                </Box>
              </>
            ) : (
              <PhotoCamera sx={{ fontSize: 18, color: "text.disabled" }} />
            )}
          </Box>
        </Tooltip>
      )}
    </ImageUploading>
  );
};
